import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { PAGE_BREAKPOINTS } from '@cat-ecom/pcc-components/src/constants/commonConstants';
import { useBreakpoint, Conditional } from '@cat-ecom/pcc-components';
import { capitalizeText } from '@cat-ecom/common/utils/commonUtils';
import {
  deselectEquipment,
  selectEquipment,
  deleteEquipment
} from 'services/equipment/actions';
import { useQuickToast } from 'hooks/use-toast';
import { getSeoUrl } from 'utils/links';
import { redirectFromEquipment } from '../utils';
import DeleteModal from '../my-equipment-modal/delete-modal';
import ValidateAssetModal from '../my-equipment-modal/validate-asset-modal';
import { useMyEquipmentDrawer } from '../my-equipment-drawer-context';
import { EditEquipmentDrawerClient } from '../edit-equipment-drawer/edit-equipment-drawer-client';
import { actionTypes, MyEquipmentKebabMenuProps } from '../types';
import EquipmentMoreActionLayout from './equipment-more-action-layout';
import ContextMenuActionLayout from './context-menu-action-layout';
import { getActionMenuSchema } from './action-menu-schema';
import { DeleteEquipmentDrawer } from '../delete-equipment-drawer/delete-equipment-drawer';
import { useMyEquipmentContext } from '../my-equipment-search-pagination-context';
import { useRequestEquipmentService } from 'components/request-equipment-service/request-equipment-service-context';
import { getRFSEnabledUser } from 'components/request-equipment-service/utils';
import { useRouter } from 'next/navigation';

export interface MyEquipmentModalProps {
  id?: string;
  isOpen: boolean;
}

export const MyEquipmentActionMenu = ({
  actionItems,
  equipmentDetails,
  userDetail,
  startTransition,
  isDetailsCard,
  gaContext,
  isAssetLandingFeatureFlag
}: Readonly<MyEquipmentKebabMenuProps>) => {
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const params = useParams();
  const esiteUrl = getSeoUrl(params);
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const t = useTranslations();
  const toast = useQuickToast();
  const { setIsRSModalOpen, setEquipmentInfo } = useRequestEquipmentService();
  const [modalOptions, setModalOptions] = useState<MyEquipmentModalProps>({
    isOpen: false
  });
  const currentEquipmentReference = useRef(null);
  const isDesktop = useBreakpoint(PAGE_BREAKPOINTS.XL);
  const { selectedEquipmentIndex } = useMyEquipmentContext();
  const isRequestService = getRFSEnabledUser(userDetail);
  const isMoreAvailable = actionItems.some(
    (cardAction) => cardAction.id === 'MORE'
  );
  const { assetName = '', id = '' } = equipmentDetails || {};
  const equipmentInfo = {
    ...(equipmentDetails ?? {}),
    assetId: assetName,
    productId: id
  };
  const router = useRouter();

  const { editEquipmentDrawer } = useMyEquipmentDrawer();

  useEffect(() => {
    if (!editEquipmentDrawer.isDrawerOpen) {
      currentEquipmentReference.current = null;
    }
  }, [editEquipmentDrawer.isDrawerOpen]);

  const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
  };

  const handleOpenContextualMenu = () => {
    setShowContextualMenu(!showContextualMenu);
  };

  const openRSModal = useCallback(() => {
    setEquipmentInfo(equipmentDetails);
    setIsRSModalOpen(true);
  }, [equipmentDetails, setEquipmentInfo, setIsRSModalOpen]);

  const editHandler = useCallback(
    (id: string) => {
      if (equipmentDetails?.isValidatedAsset) {
        setModalOptions({ id, isOpen: true });
      } else {
        editEquipmentDrawer.openDrawer(null, false);
        currentEquipmentReference.current = equipmentInfo;
        setModalOptions({ id, isOpen: false });
      }
      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editEquipmentDrawer, equipmentInfo]
  );

  const redirectOnClick = (url: string, isSelectedEquipment = true) => {
    redirectFromEquipment(url, isSelectedEquipment, equipmentDetails);
  };

  const getToastTransitionText = (text: string) =>
    t(text, {
      0: equipmentDetails?.model,
      1: capitalizeText(equipmentDetails?.productFamily)
    });

  const clearSelectionHandler = () => {
    startTransition(async () => {
      const { status } = await deselectEquipment();
      if (status === 'success') {
        toast.success({
          message: getToastTransitionText(
            'REMOVE_FILTER_EQUIPMENT_SUCCESS_TOAST'
          ),
          delay: 3
        });
      } else if (status === 'error') {
        toast.error({
          message: t('UNKONWN_ERROR'),
          delay: 3
        });
      }
    });
  };

  const selectedEquipmentHandler = () => {
    startTransition(async () => {
      await selectEquipment(
        equipmentDetails?.id,
        equipmentDetails?.serialNumber
      );
    });
  };

  const additionalActionProps = (action: actionTypes) => {
    const actionsProps = getActionMenuSchema({
      action,
      equipmentDetails,
      isRequestService,
      isDetailsCard,
      actionItems,
      selectedEquipmentIndex,
      isDesktop,
      esiteUrl,
      setModalOptions,
      openRSModal,
      editHandler,
      clearSelectionHandler,
      selectedEquipmentHandler,
      redirectOnClick,
      gaContext,
      params,
      router,
      isAssetLandingFeatureFlag
    });

    return actionsProps;
  };

  const equipmentActions = actionItems.map((cardAction) => {
    return additionalActionProps(cardAction);
  });

  const deleteActionHandler = async () => {
    setModalOptions({ isOpen: false });
    startTransition(async () => {
      const { status } = await deleteEquipment(
        equipmentDetails?.unvalidatedItemListId,
        equipmentDetails?.serialNumber ?? ''
      );
      if (status === 'success') {
        toast.warning({
          message: getToastTransitionText('MEQ_DELETE_MESSAGE'),
          delay: 5
        });
      }
    });
  };

  return (
    <>
      {!isMoreAvailable ? (
        <ContextMenuActionLayout
          isDesktop={isDesktop}
          actionItems={equipmentActions}
          showContextualMenu={showContextualMenu}
          setShowContextualMenu={setShowContextualMenu}
          handleClick={handleClick}
          handleOpenContextualMenu={handleOpenContextualMenu}
        />
      ) : (
        equipmentActions.map((item) => {
          return (
            item.isActionVisible && (
              <EquipmentMoreActionLayout cardAction={item} key={item.id} />
            )
          );
        })
      )}

      <DeleteModal
        equipmentDetails={equipmentDetails}
        isOpen={modalOptions.isOpen && modalOptions.id === 'DELETE'}
        closeModal={() => {
          setModalOptions({ isOpen: false });
        }}
        deleteActionHandler={deleteActionHandler}
      />

      <ValidateAssetModal
        isActive={modalOptions.isOpen && modalOptions.id === 'EDIT'}
        closeEditModal={() => {
          setModalOptions({ isOpen: false });
        }}
      />

      <Conditional test={currentEquipmentReference.current}>
        <div role="none" onClick={handleClick} onKeyDown={handleClick}>
          <EditEquipmentDrawerClient
            selectedEquipmentInfo={currentEquipmentReference.current ?? {}}
            isLoggedIn={userDetail?.isLoggedIn}
          />
          <DeleteEquipmentDrawer
            selectedEquipmentInfo={currentEquipmentReference.current ?? {}}
          />
        </div>
      </Conditional>
    </>
  );
};
